import React, { ComponentType, ReactElement, Ref, forwardRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ActionButtonComponentProps,
  ActionButtonHandle,
  DetailMode,
  FormTextArea,
  abortableFetch,
  useEventCallback,
} from '@eas/common-web';
import { DetailActionButton } from '@components/action-button/detail-action-button';
import { ViewingRequest } from '@models';
import { EvidenceApiUrl } from '@enums';

export function useChangeViewingRequestStateDialog() {
  const callApi = useEventCallback(
    (id: string, newState: 'approve' | 'reject', reason?: string) => {
      return abortableFetch(
        `${EvidenceApiUrl.VIEWING_REQUEST}/${id}/${newState}`,
        {
          method: 'PUT',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            reason,
          }),
        }
      );
    }
  );

  return {
    callApi,
  };
}

export const ApproveViewingRequestButton = forwardRef(
  function ApproveViewingRequestButton(
    {
      ButtonComponent,
      modes,
      viewingRequest,
    }: {
      ButtonComponent?: ComponentType<ActionButtonComponentProps>;
      modes?: DetailMode[];
      viewingRequest: ViewingRequest;
    },
    ref: Ref<ActionButtonHandle>
  ) {
    const intl = useIntl();
    const { callApi } = useChangeViewingRequestStateDialog();

    return (
      <DetailActionButton
        ref={ref}
        promptKey="PROCESS_VIEWING_REQUEST"
        apiCall={() => callApi(viewingRequest.id, 'approve')}
        buttonLabel={intl.formatMessage({
          id: 'ES__VIEWING_REQUESTS_TOOLBAR__APPROVE_VIEWING_REQUEST',
          defaultMessage: 'Schválit',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__VIEWING_REQUESTS_TOOLBAR__PROCESS_VIEWING_REQUEST__TITLE',
          defaultMessage: 'Žádost o nahlížení',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__VIEWING_REQUESTS_TOOLBAR__APPROVE_VIEWING_REQUEST__TEXT',
          defaultMessage: 'Skutečně chcete schválit tuto žádost o nahlížení?',
        })}
        ButtonComponent={ButtonComponent}
        modes={modes}
      />
    );
  }
) as (
  p: {
    ButtonComponent?: ComponentType<ActionButtonComponentProps>;
    modes?: DetailMode[];
    viewingRequest: ViewingRequest;
  } & {
    ref?: Ref<ActionButtonHandle>;
  }
) => ReactElement;

export function RejectViewingRequestFields() {
  return (
    <>
      <FormTextArea
        name="reason"
        label={
          <FormattedMessage
            id="ES__VIEWING_REQUESTS_TOOLBAR__REJECT_VIEWING_REQUEST__REASON"
            defaultMessage="Důvod zamítnutí"
          />
        }
        autoFocus={true}
        labelOptions={{
          hide: true,
        }}
      />
    </>
  );
}

export const RejectViewingRequestButton = forwardRef(
  function RejectViewingRequestButton(
    {
      ButtonComponent,
      modes,
      viewingRequest,
    }: {
      modes?: DetailMode[];
      viewingRequest: ViewingRequest;
      ButtonComponent?: ComponentType<ActionButtonComponentProps>;
    },
    ref: Ref<ActionButtonHandle>
  ) {
    const intl = useIntl();
    const { callApi } = useChangeViewingRequestStateDialog();

    return (
      <DetailActionButton
        ref={ref}
        promptKey="REJECT_VIEWING_REQUEST"
        apiCall={(
          _id: string,
          formData: {
            reason: string;
          }
        ) => callApi(viewingRequest.id, 'reject', formData.reason)}
        buttonLabel={intl.formatMessage({
          id: 'ES__ES_FILES__VIEWING_REQUESTS_TOOLBAR__REJECT_VIEWING_REQUEST',
          defaultMessage: 'Zamítnout',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__ES_FILES__DIALOG_REJECT_VIEWING_REQUEST__TITLE',
          defaultMessage: 'Žádost o nahlížení',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__ES_FILES__DIALOG_REJECT_VIEWING_REQUEST__TEXT',
          defaultMessage:
            'Skutečně chcete zamítnout tuto žádost o nahlížení? Uveďte důvod zamítnutí.',
        })}
        ButtonComponent={ButtonComponent}
        modes={modes}
        FormFields={RejectViewingRequestFields}
        dialogWidth={600}
      />
    );
  }
) as (
  p: {
    ButtonComponent?: ComponentType<ActionButtonComponentProps>;
    modes?: DetailMode[];
    viewingRequest: ViewingRequest;
  } & {
    ref?: Ref<ActionButtonHandle>;
  }
) => ReactElement;
